import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import { Typography } from 'gd-design-library';
import { RichText as RichTextI } from '@services';
import styles from './RichText.module.scss';

const CN = 'rich-text';
const paragraphClassName = `${CN}__paragraph`;
const headingClassName = `${CN}__heading`;
const linkClassName = `${CN}__link`;
const textClassName = `${CN}__text`;
const boldClassName = `${CN}__bold`;
const italicClassName = `${CN}__italic`;
const underlineClassName = `${CN}__underline`;
const cx = classNames.bind(styles);

interface Props {
  richText: RichTextI;
  className?: string;
}

export const RichText: React.FC<Props> = ({ richText, className }) => {
  const renderRichTextRecursively = (richTextSegment: RichTextI, skipTypography?: boolean): ReactElement | undefined => {
    if (richTextSegment.children) {
      switch (richTextSegment.type) {
      case 'a':
        return (
          <a href={ richTextSegment.attrs?.url } className={ cx(linkClassName) }>
            <Typography
              className={ cx(textClassName, {
                [boldClassName]: richTextSegment.bold,
                [italicClassName]: richTextSegment.italic,
                [underlineClassName]: richTextSegment.underline
              }) }
            >
              {richTextSegment.children.map(child => renderRichTextRecursively(child, true))}
            </Typography>
          </a>
        );
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return (
          <Typography
            variant={ richTextSegment.type }
            className={ cx(headingClassName, {
              [boldClassName]: richTextSegment.bold,
              [italicClassName]: richTextSegment.italic,
              [underlineClassName]: richTextSegment.underline
            }) }
          >
            {richTextSegment.children.map(child => renderRichTextRecursively(child, true))}
          </Typography>
        );
      case 'doc':
      case 'p':
        return (
          <div className={ cx(paragraphClassName) }>
            {richTextSegment.children.map(child => renderRichTextRecursively(child))}
          </div>
        );
      default:
        return (
          <>
            {richTextSegment.children.map(child => renderRichTextRecursively(child))}
          </>
        );
      }
    } else if (richTextSegment.text !== undefined) {
      return (
        <>
          {
            !skipTypography ? <Typography
              className={ cx(textClassName, {
                [boldClassName]: richTextSegment.bold,
                [italicClassName]: richTextSegment.italic,
                [underlineClassName]: richTextSegment.underline
              }) }
            >
              {richTextSegment.text}
            </Typography> : richTextSegment.text
          }
          {richTextSegment.break ? <br /> : null}
        </>
      );
    }
  };

  return (
    <div className={ cx(CN, className) }>
      {renderRichTextRecursively(richText)}
    </div>
  );
};
